import type { Component } from 'react';
import { Fragment, useEffect, useState } from 'react';

import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import { withAlignment } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import AccountIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/account';
import AirlineSingleIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/airline';
import AirlineIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/airline--multiple';
import AirportsIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/airports';
import BaggageCheckedIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/baggage-checked';
import BaggageGenericIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/baggage-generic';
import CalendarIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/calendar';
import Cars from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/cars';
import CenterLocation from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/center-location';
import CityIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/city';
import CityCenterIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/city-center';
import DepartIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/depart';
import DurationIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/duration';
import FlagIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flag';
import FlightIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flight';
import FlightTakeoffIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flight-takeoff';
import FoodIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/food';
import BpkGearsManualCircleLargeIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/gears-manual-circle';
import HeartIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/heart';
import HotelsIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/hotels';
import HotelsPetsAllowedIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/hotels--pets-allowed';
import InformationLanguageIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/information--language';
import Insurance from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/insurance';
import LanguageIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/language';
import LegroomExtraIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/legroom--extra';
import Location from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/location';
import LoungeIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/lounge';
import MealIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/meal';
import MoneyIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/money';
import PaidIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/paid';
import PlaneSeatIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/plane-seat';
import PriceTagIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/price-tag';
import SocialLikeIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/social-like';
import StarIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/star';
import SwapHorizontalIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/swap--horizontal';
import ThumbsUpIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/thumbs-up';
import TimeIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/time';
import TrendIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/trend';
import TrendDownIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/trend--down';
import Wallet from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/wallet';
import WeatherIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/weather';
import WeatherCloudyIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/weather--cloudy';
import WeatherPartlyCloudyIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/weather--partly-cloudy';
import WeatherRainIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/weather--rain';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import {
  BpkTableRow,
  BpkTableCell,
  BpkTableHeadCell,
} from '@skyscanner-internal/backpack-web/bpk-component-table';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { GLOBAL } from 'saddlebag-browser';

import {
  iconSizeLg,
  lineHeightBase,
} from '@skyscanner/bpk-foundations-web/tokens/base.es6';

import { anchorScroll } from '../../utils';
import { useMBDContext } from '../common/MBD/useMBDContext';

import TemperatureIcon from './icons/TemperatureIcon';

import type { SnippetProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/InfoSnippetDecoupledProps';

import STYLES from './Snippet.module.scss';

export const iconsMap: { [key: string]: typeof Component } = {
  temperature: withAlignment(TemperatureIcon, lineHeightBase, iconSizeLg),
  flight: withAlignment(FlightIcon, lineHeightBase, iconSizeLg),
  trend: withAlignment(TrendIcon, lineHeightBase, iconSizeLg),
  trendDown: withAlignment(TrendDownIcon, lineHeightBase, iconSizeLg),
  money: withAlignment(MoneyIcon, lineHeightBase, iconSizeLg),
  time: withAlignment(TimeIcon, lineHeightBase, iconSizeLg),
  calendar: withAlignment(CalendarIcon, lineHeightBase, iconSizeLg),
  airline: withAlignment(AirlineIcon, lineHeightBase, iconSizeLg),
  airlineSingle: withAlignment(AirlineSingleIcon, lineHeightBase, iconSizeLg),
  duration: withAlignment(DurationIcon, lineHeightBase, iconSizeLg),
  flag: withAlignment(FlagIcon, lineHeightBase, iconSizeLg),
  city: withAlignment(CityIcon, lineHeightBase, iconSizeLg),
  cityCenter: withAlignment(CityCenterIcon, lineHeightBase, iconSizeLg),
  priceTag: withAlignment(PriceTagIcon, lineHeightBase, iconSizeLg),
  paid: withAlignment(PaidIcon, lineHeightBase, iconSizeLg),
  depart: withAlignment(DepartIcon, lineHeightBase, iconSizeLg),
  airports: withAlignment(AirportsIcon, lineHeightBase, iconSizeLg),
  baggageChecked: withAlignment(BaggageCheckedIcon, lineHeightBase, iconSizeLg),
  baggageGeneric: withAlignment(BaggageGenericIcon, lineHeightBase, iconSizeLg),
  heart: withAlignment(HeartIcon, lineHeightBase, iconSizeLg),
  socialLike: withAlignment(SocialLikeIcon, lineHeightBase, iconSizeLg),
  thumbsUp: withAlignment(ThumbsUpIcon, lineHeightBase, iconSizeLg),
  star: withAlignment(StarIcon, lineHeightBase, iconSizeLg),
  hotelsPetsAllowed: withAlignment(
    HotelsPetsAllowedIcon,
    lineHeightBase,
    iconSizeLg,
  ),
  hotel: withAlignment(HotelsIcon, lineHeightBase, iconSizeLg),
  hotels: withAlignment(HotelsIcon, lineHeightBase, iconSizeLg),
  insurance: withAlignment(Insurance, lineHeightBase, iconSizeLg),
  cars: withAlignment(Cars, lineHeightBase, iconSizeLg),
  centerLocation: withAlignment(CenterLocation, lineHeightBase, iconSizeLg),
  location: withAlignment(Location, lineHeightBase, iconSizeLg),
  lounge: withAlignment(LoungeIcon, lineHeightBase, iconSizeLg),
  account: withAlignment(AccountIcon, lineHeightBase, iconSizeLg),
  language: withAlignment(LanguageIcon, lineHeightBase, iconSizeLg),
  legroomExtra: withAlignment(LegroomExtraIcon, lineHeightBase, iconSizeLg),
  food: withAlignment(FoodIcon, lineHeightBase, iconSizeLg),
  meal: withAlignment(MealIcon, lineHeightBase, iconSizeLg),
  planeSeat: withAlignment(PlaneSeatIcon, lineHeightBase, iconSizeLg),
  informationLanguage: withAlignment(
    InformationLanguageIcon,
    lineHeightBase,
    iconSizeLg,
  ),
  gearsManualCircle: withAlignment(
    BpkGearsManualCircleLargeIcon,
    lineHeightBase,
    iconSizeLg,
  ),
  wallet: withAlignment(Wallet, lineHeightBase, iconSizeLg),
  weatherPartlyCloudy: withAlignment(
    WeatherPartlyCloudyIcon,
    lineHeightBase,
    iconSizeLg,
  ),
  weatherRain: withAlignment(WeatherRainIcon, lineHeightBase, iconSizeLg),
  flightTakeoff: withAlignment(FlightTakeoffIcon, lineHeightBase, iconSizeLg),
  swapHorizontal: withAlignment(SwapHorizontalIcon, lineHeightBase, iconSizeLg),
  weather: withAlignment(WeatherIcon, lineHeightBase, iconSizeLg),
  weatherCloudy: withAlignment(WeatherCloudyIcon, lineHeightBase, iconSizeLg),
};

const EmptyIcon = () => null;
const CONTENT_TYPE = {
  BUTTON: 'button',
  LINK: 'link',
  LIST: 'list',
  TEXT: 'text',
  HTML: 'html',
};
const $document = GLOBAL.getDocument();

const isHtmlSnippet = (str: string) => {
  const htmlTagPattern = /<[^>]+>/;
  return htmlTagPattern.test(str);
};
const Snippet = ({
  ariaText,
  dofollow,
  icon,
  id,
  isHtml,
  label,
  linkData,
  linkUrl,
  mbdParams,
  scrollId,
  snippetId,
  titleData = {},
  value,
  valueSecondLine,
  withItemsBackground = false,
}: SnippetProps) => {
  const Icon = iconsMap[icon] || EmptyIcon;
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const MBD_COMPONENT_NAME = `${rawComponentName}_item`;
  const snippetContainHtml =
    typeof value === 'string' ? isHtmlSnippet(value) : false;

  const trackingProperties = {
    'data-tracking-element-id': `${MBD_COMPONENT_NAME}_${snippetId}`,
    // `snippet_id` is derived automatically from the existing `snippetId`
    // prop so callers don't need to duplicate it inside `mbdParams` (which
    // would risk the two drifting out of sync). Callers can still use
    // `mbdParams` to add extra semantic fields (e.g. `snippet_type`).
    // `snippet_id` is spread last so it always reflects the real `snippetId`
    // even if a caller's `mbdParams` mistakenly also sets a `snippet_id` key.
    'data-tracking-common-params': buildMBDParam(MBD_COMPONENT_NAME, {
      ...mbdParams,
      snippet_id: String(snippetId),
    }),
  };

  let type;
  if (linkData) {
    type = CONTENT_TYPE.LIST;
  } else if (isHtml || snippetContainHtml) {
    type = CONTENT_TYPE.HTML;
  } else if (linkUrl) {
    type = CONTENT_TYPE.LINK;
  } else {
    type = CONTENT_TYPE.TEXT;
  }

  const [contentType, setContentType] = useState(type);

  const onScrollClick = () => {
    anchorScroll(scrollId);
  };

  useEffect(() => {
    if (scrollId && !!$document.getElementById(scrollId)) {
      setContentType(CONTENT_TYPE.BUTTON);
    }
  }, [scrollId]);

  let cellContent;
  if (contentType === CONTENT_TYPE.LIST) {
    cellContent = linkData?.map((linkItem: any, index: number) => (
      <Fragment key={linkItem.airportName}>
        {index > 0 && ', '}
        {linkItem.link ? (
          <BpkLink
            {...trackingProperties}
            // Override id becuse this is an array and we need to ensure unique ids
            data-tracking-element-id={`${MBD_COMPONENT_NAME}_${snippetId}_${index}`}
            data-testid="linkurl"
            href={linkItem.link}
            aria-label={ariaText}
            className={STYLES.Snippet__clickAndValueLink}
            {...titleData}
          >
            {linkItem.airportName}
          </BpkLink>
        ) : (
          <BpkText
            data-testid="subtext"
            textStyle={TEXT_STYLES.heading4}
            className={STYLES.Snippet__clickAndValue}
            {...titleData}
          >
            {linkItem.airportName}
          </BpkText>
        )}
      </Fragment>
    ));
  } else if (contentType === CONTENT_TYPE.BUTTON) {
    cellContent = (
      <BpkButton
        {...trackingProperties}
        aria-label={ariaText}
        className={STYLES.Snippet__clickAndValue}
        onClick={() => onScrollClick()}
        type={BUTTON_TYPES.link}
        implicit
      >
        <span className={STYLES.Snippet__valueText}>{value}</span>
      </BpkButton>
    );
  } else if (contentType === CONTENT_TYPE.LINK) {
    cellContent = (
      <BpkLink
        {...trackingProperties}
        data-testid="linkurl"
        href={linkUrl}
        {...(dofollow ? {} : { rel: 'nofollow' })}
        aria-label={ariaText}
        className={STYLES.Snippet__clickAndValueLink}
        {...titleData}
      >
        {value}
      </BpkLink>
    );
  } else if (contentType === CONTENT_TYPE.HTML) {
    cellContent = (
      <div>
        <BpkText
          {...trackingProperties}
          data-testid="subtext"
          textStyle={TEXT_STYLES.heading4}
          className={STYLES.Snippet__clickAndValue}
          {...titleData}
          dangerouslySetInnerHTML={{
            __html: value,
          }}
        />
      </div>
    );
  } else if (contentType === CONTENT_TYPE.TEXT) {
    cellContent = (
      <>
        <BpkText
          {...trackingProperties}
          data-testid="subtext"
          textStyle={TEXT_STYLES.heading4}
          className={STYLES.Snippet__clickAndValue}
          {...titleData}
        >
          {value}
        </BpkText>
        {valueSecondLine && (
          <div className={STYLES.Snippet__valueSecondLineWrapper}>
            <BpkText
              className={STYLES.Snippet__valueSecondLine}
              textStyle={TEXT_STYLES.bodyDefault}
            >
              {valueSecondLine}
            </BpkText>
          </div>
        )}
      </>
    );
  }

  return (
    <BpkTableRow
      className={`${STYLES.Snippet} ${
        withItemsBackground ? STYLES.Snippet__withBackground : ''
      }`}
      data-testid={id}
    >
      <BpkTableHeadCell className={STYLES.Snippet__row} aria-label={label}>
        <Icon className={STYLES.Snippet__icon} />
        <BpkText
          data-testid="snippet-header"
          color={TEXT_COLORS.textPrimary}
          className={STYLES.label}
          textStyle={TEXT_STYLES.bodyDefault}
        >
          {label}
        </BpkText>
      </BpkTableHeadCell>
      <BpkTableCell className={STYLES.Snippet__row}>{cellContent}</BpkTableCell>
    </BpkTableRow>
  );
};

export default Snippet;
